'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type { StoreReview } from '@eliadmin/core/src/api/general/general-api';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';

import { motion, useAnimationControls } from 'framer-motion';
import { FaStar } from 'react-icons/fa';
import { HtmlDescription } from '../ui/html-description';

export function Reviews({
  subtitle,
  title,
  reviews,
  speed = 180,
}: {
  subtitle: string;
  title: string;
  reviews: StoreReview[];
  speed?: number;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const controls = useAnimationControls();
  const [trackWidth, setTrackWidth] = useState(0);
  const [isReducedMotion, setReducedMotion] = useState(false);

  const duplicated = [...reviews, ...reviews, ...reviews];

  const measure = useCallback(() => {
    if (!trackRef.current) return;
    const third = trackRef.current.scrollWidth / 3;
    setTrackWidth(third);
  }, []);

  useEffect(() => {
    measure();
    const handler = () => measure();
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [measure]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(media.matches);

    const listener = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, []);

  useEffect(() => {
    if (!trackWidth || isReducedMotion) return;

    const duration = speed;

    controls.set({ x: 0 });
    controls.start({
      x: -trackWidth,
      transition: {
        duration,
        ease: 'linear',
        repeat: Infinity,
        repeatType: 'loop',
      },
    });
  }, [trackWidth, speed, controls, isReducedMotion]);

  return (
    <section className={cn('custom-section', 'gap-8 px-0 py-10 sm:gap-10 md:py-16 lg:gap-12')}>
      <Heading
        subtitle={subtitle}
        title={title}
        className={{ container: 'items-center justify-center px-4 text-center' }}
      />
      <motion.div
        ref={trackRef}
        className="flex"
        style={{ willChange: 'transform' }}
        animate={controls}
      >
        {duplicated.map((review, idx) => (
          <div
            key={`${review.id}-${idx}`}
            className="mx-2 flex-shrink-0 sm:mx-3 lg:mx-4"
            style={{ width: 'fit-content' }}
          >
            <ReviewCard review={review} />
          </div>
        ))}
      </motion.div>
      <div className="flex w-full items-center justify-center px-4">
        <Button asChild variant={'dark'} className="w-full sm:w-fit">
          <Link
            prefetch={false}
            href={
              'https://www.google.com/search?sca_esv=940aa337a8577638&sxsrf=AE3TifMzsAZ8_2n5qGQD-NVgO9kwJS-gdw:1764723747706&si=AMgyJEtREmoPL4P1I5IDCfuA8gybfVI2d5Uj7QMwYCZHKDZ-E0dH0ebiCz1yUvQ7P7xZGxLSJZSZnyQzDZggwDH_i5WXSOGWz53UosvAKJMc6AR-xmyLeDHntSpmIb0gDnBVfnOMauMvNKGGSFQ3pOsBuNmWdpCJjzAF6ngzjf9YuVv3_ejYyKnyHcq_x6_Q1ZSbieAqrnQj&q=KADERN%C3%8DCTVO+KAKADU+-+Detsk%C3%A9,+d%C3%A1mske+a+p%C3%A1nske+kadern%C3%ADctvo+Recenzie&sa=X&ved=2ahUKEwjfie-gnKCRAxXgGBAIHTPXANMQ0bkNegQIIhAE&biw=2133&bih=1050&dpr=0.9'
            }
            target="_blank"
          >
            <Image
              src={'/images/pen.svg'}
              alt=""
              width={20}
              height={20}
              className="object-contain"
            />
            Napísať recenziu
          </Link>
        </Button>
      </div>
    </section>
  );
}

function ReviewCard({ review }: { review: StoreReview }) {
  return (
    <div className="flex h-full w-[340px] flex-col items-start justify-start gap-4 rounded-[24px] border border-black/10 p-4 sm:w-[460px] sm:gap-6 sm:p-5 md:w-[520px] lg:w-[588px] lg:gap-8 lg:p-6">
      <div className="flex w-full items-center justify-between">
        <p className="text-base font-bold text-black sm:text-lg lg:text-xl">{review.name}</p>
        <div className="flex items-center justify-end gap-0.5">
          {[...Array(5)].map((_, i) => (
            <FaStar key={i} className="text-primary" size={20} />
          ))}
        </div>
      </div>
      <div className="h-[1px] w-full bg-black/10" />
      <HtmlDescription
        content={review.review}
        className={cn('w-full', 'ea-review-card__description')}
        color="black"
      />
    </div>
  );
}
